import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts';
import {
  Users, Mail, Phone, Globe, MessageSquare, Eye,
  TrendingUp, Loader2, RefreshCw, Send, Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { adminGetAnalytics, adminSendTradieReport, adminSendAllTradieReports } from '@/lib/api';

const RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
];

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// Default to previous calendar month (when reporting on a completed month makes sense)
const prevMonthDefault = () => {
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth(); // 0-indexed current month -> previous month is 0-11 = 1-12
  if (m === 0) { m = 12; y -= 1; }
  return { year: y, month: m };
};

// Build a list of the most recent 12 months as {value: 'YYYY-MM', label: 'Mon YYYY'}
const monthOptions = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    opts.push({ value: `${y}-${String(m).padStart(2, '0')}`, label: `${MONTH_NAMES[m]} ${y}`, year: y, month: m });
  }
  return opts;
};

const STATUS_COLORS = {
  active: '#16a34a',
  trial: '#e04f39',
  cancelled: '#9ca3af',
  inactive: '#d1d5db',
  free: '#00263a',
};

const formatShort = (dateStr) => {
  // "2026-06-12" -> "12 Jun"
  if (!dateStr || dateStr.length < 10) return dateStr;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' });
};

// Merge multiple per-day series into a single recharts-friendly array
const mergeSeries = (seriesMap) => {
  const dateSet = new Set();
  Object.values(seriesMap).forEach((arr) => arr.forEach((p) => dateSet.add(p.date)));
  const dates = Array.from(dateSet).sort();
  return dates.map((d) => {
    const row = { date: d, label: formatShort(d) };
    Object.entries(seriesMap).forEach(([key, arr]) => {
      const match = arr.find((p) => p.date === d);
      row[key] = match ? match.count : 0;
    });
    return row;
  });
};

const StatCard = ({ icon: Icon, label, value, accent = 'primary' }) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent === 'secondary' ? 'bg-secondary/10 text-secondary' : 'bg-primary/10 text-primary'}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-heading font-semibold text-foreground">{value}</div>
      </div>
    </CardContent>
  </Card>
);

const AdminAnalytics = ({ token }) => {
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reports state
  const monthOpts = monthOptions();
  const prev = prevMonthDefault();
  const [reportMonth, setReportMonth] = useState(`${prev.year}-${String(prev.month).padStart(2, '0')}`);
  const [sendingIndividual, setSendingIndividual] = useState(null); // tradie_id being sent
  const [sendingAll, setSendingAll] = useState(false);
  const [onlyWithActivity, setOnlyWithActivity] = useState(true);

  const selectedMonth = monthOpts.find((o) => o.value === reportMonth) || monthOpts[0];

  const handleSendIndividual = async (tradieId, tradieName) => {
    setSendingIndividual(tradieId);
    try {
      const res = await adminSendTradieReport(token, tradieId, selectedMonth.year, selectedMonth.month);
      toast.success(`${selectedMonth.label} report queued for ${tradieName}`, { description: res.email });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send report';
      toast.error(msg);
    } finally {
      setSendingIndividual(null);
    }
  };

  const handleSendAll = async () => {
    setSendingAll(true);
    try {
      const res = await adminSendAllTradieReports(token, selectedMonth.year, selectedMonth.month, onlyWithActivity);
      toast.success(`${res.queued} ${selectedMonth.label} reports queued`, {
        description: res.skipped ? `${res.skipped} skipped (no activity or no email)` : 'Sent to all approved tradies'
      });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send reports';
      toast.error(msg);
    } finally {
      setSendingAll(false);
    }
  };

  const fetchData = async (rangeDays) => {
    setLoading(true);
    try {
      const result = await adminGetAnalytics(token, parseInt(rangeDays, 10));
      setData(result);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await adminGetAnalytics(token, parseInt(days, 10));
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, days]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  // Headline cards
  const totals = data.totals || {};

  // Combined daily activity chart: signups + enquiries
  const activitySeries = mergeSeries({
    signups: data.signups_by_day || [],
    enquiries: data.enquiries_by_day || [],
  });

  // Combined click chart: phone + email + website
  const clickSeries = mergeSeries({
    phone: data.phone_clicks_by_day || [],
    email: data.email_clicks_by_day || [],
    website: data.website_clicks_by_day || [],
  });

  // Engagement chart: enquiry button opens + card expands
  const engagementSeries = mergeSeries({
    enquiry_button: data.enquiry_opens_by_day || [],
    card_open: data.card_expands_by_day || [],
  });

  // Subscription breakdown -> pie data
  const sub = data.subscription_breakdown || {};
  const subData = [
    { name: 'Active', value: sub.active || 0, color: STATUS_COLORS.active },
    { name: 'Trial', value: sub.trial || 0, color: STATUS_COLORS.trial },
    { name: 'Free', value: sub.free || 0, color: STATUS_COLORS.free },
    { name: 'Cancelled', value: sub.cancelled || 0, color: STATUS_COLORS.cancelled },
    { name: 'Inactive', value: sub.inactive || 0, color: STATUS_COLORS.inactive },
  ].filter((s) => s.value > 0);

  const leaderboard = data.leaderboard || [];

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-foreground">Analytics</h2>
          <p className="text-sm text-muted-foreground">Real-time interactions on the directory ({data.range_days}-day window)</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[180px]" data-testid="analytics-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => fetchData(days)} disabled={loading} data-testid="analytics-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Headline stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard icon={Users} label="Signups" value={totals.signups || 0} />
        <StatCard icon={MessageSquare} label="Enquiries" value={totals.enquiries || 0} accent="secondary" />
        <StatCard icon={Mail} label="Enquiry Btn Opens" value={totals.enquiry_opens || 0} />
        <StatCard icon={Eye} label="Listing Opens" value={totals.card_expands || 0} />
        <StatCard icon={Phone} label="Phone Clicks" value={totals.phone_clicks || 0} accent="secondary" />
        <StatCard icon={Mail} label="Email Clicks" value={totals.email_clicks || 0} accent="secondary" />
        <StatCard icon={Globe} label="Website Clicks" value={totals.website_clicks || 0} accent="secondary" />
      </div>

      {/* Daily activity chart */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Daily Activity
          </CardTitle>
          <CardDescription>New tradie signups & enquiries submitted per day</CardDescription>
        </CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={activitySeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#57534e' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#57534e' }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="signups" name="Signups" stroke="#00263a" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="enquiries" name="Enquiries" stroke="#e04f39" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Contact clicks chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              Contact Clicks
            </CardTitle>
            <CardDescription>Phone, email & website clicks per day</CardDescription>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={clickSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#57534e' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#57534e' }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="phone" name="Phone" stroke="#00263a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="email" name="Email" stroke="#e04f39" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="website" name="Website" stroke="#16a34a" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Engagement chart */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Listing Engagement
            </CardTitle>
            <CardDescription>&ldquo;View Full Details&rdquo; opens &amp; &ldquo;Send Enquiry&rdquo; button clicks per day</CardDescription>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={engagementSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#57534e' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#57534e' }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="card_open" name="Listing opened" stroke="#00263a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="enquiry_button" name="Enquiry button" stroke="#e04f39" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Subscription breakdown */}
      {subData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Subscription Status (Live Snapshot)</CardTitle>
            <CardDescription>Current breakdown of all tradies (not limited to the date range)</CardDescription>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={subData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {subData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Monthly Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Monthly Email Reports
          </CardTitle>
          <CardDescription>Send each tradie a personalised performance report (logo, enquiries, listing opens, contact clicks).</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="report-month" className="text-xs uppercase tracking-wide text-muted-foreground">Reporting period</Label>
              <Select value={reportMonth} onValueChange={setReportMonth}>
                <SelectTrigger className="w-[200px]" id="report-month" data-testid="report-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOpts.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 ml-2">
              <Switch id="only-with-activity" checked={onlyWithActivity} onCheckedChange={setOnlyWithActivity} data-testid="only-with-activity-switch" />
              <Label htmlFor="only-with-activity" className="text-sm cursor-pointer">Skip tradies with no activity</Label>
            </div>
            <div className="flex-1" />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" disabled={sendingAll} data-testid="send-all-reports-btn">
                  {sendingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send to all approved tradies
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Send {selectedMonth.label} report to all approved tradies?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Each tradie will receive a personalised email summarising their listing performance for {selectedMonth.label}.
                    {onlyWithActivity ? ' Tradies with zero activity will be skipped.' : ' All approved tradies will be emailed, even those with zero activity.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleSendAll}>Send</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Top Tradies by Interaction</CardTitle>
          <CardDescription>Combined enquiries + contact clicks within the selected window</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {leaderboard.length === 0 ? (
            <div className="px-6 py-10 text-center text-muted-foreground text-sm">
              No interactions recorded in this window yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tradie</TableHead>
                  <TableHead className="text-right">Enquiries</TableHead>
                  <TableHead className="text-right">Enquiry Btn</TableHead>
                  <TableHead className="text-right">Listing Opens</TableHead>
                  <TableHead className="text-right">Phone</TableHead>
                  <TableHead className="text-right">Email</TableHead>
                  <TableHead className="text-right">Website</TableHead>
                  <TableHead className="text-right font-semibold">Total</TableHead>
                  <TableHead className="text-right">Report</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaderboard.map((row) => (
                  <TableRow key={row.tradie_id} data-testid={`analytics-row-${row.tradie_id}`}>
                    <TableCell className="font-medium">{row.tradie_name || '(unknown)'}</TableCell>
                    <TableCell className="text-right">{row.enquiries}</TableCell>
                    <TableCell className="text-right">{row.enquiry_opens}</TableCell>
                    <TableCell className="text-right">{row.card_expands}</TableCell>
                    <TableCell className="text-right">{row.phone_clicks}</TableCell>
                    <TableCell className="text-right">{row.email_clicks}</TableCell>
                    <TableCell className="text-right">{row.website_clicks}</TableCell>
                    <TableCell className="text-right font-semibold text-primary">{row.total_interactions}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendIndividual(row.tradie_id, row.tradie_name)}
                        disabled={sendingIndividual === row.tradie_id}
                        data-testid={`send-report-${row.tradie_id}`}
                        title={`Send ${selectedMonth.label} report`}
                      >
                        {sendingIndividual === row.tradie_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalytics;
